import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useAgencies from '@hooks/useAgencies'
import useBrandThemes from '@hooks/useBrandThemes'
import useProviders from '@hooks/useProviders'
import usePublications from '@hooks/usePublications'
import useServices from '@hooks/useServices'
import useServiceProducts from '@hooks/useServiceProducts'
import useTemplateGroups from '@hooks/useTemplateGroups'

import type {
  AgencyModel, BrandThemeModel, ProviderModel, PublicationModel, ServiceModel, ServiceProductModel, TemplateGroupModel,
} from '@domain-group/realhub-backend-api-types'

import type { Permissions } from './useAssignTemplatePagePermissionsModal'

const watchEntityKeys = [
  'agencies',
  'brandThemes',
  'providers',
  'publications',
  'services',
  'serviceProducts',
  'templateGroups',
]

export type FilteredEntities = {
  agencies: AgencyModel[],
  brandThemes: BrandThemeModel[],
  providers: ProviderModel[],
  publications: PublicationModel[],
  services: ServiceModel[],
  serviceProducts: ServiceProductModel[],
  templateGroups: TemplateGroupModel[],
}

type UseScopeEntitiesOptions = {
  activeStepKey: string,
  filters: {
    brand_id?: number,
    service_id?: number,
    string: string,
  },
  permissions: Permissions,
}

function useScopedEntities(options: UseScopeEntitiesOptions) {
  const {
    activeStepKey,
    filters: {
      brand_id,
      service_id,
      string,
    },
    permissions: {
      current: {
        serviceIds: currentServiceIds,
      },
      queued: {
        providerIds: queuedProviderIds,
        serviceIds: queuedServiceIds,
      },
    },
  } = options

  const hasClearedServices = !!currentServiceIds.length && !queuedServiceIds.length
  const serviceProductServiceIds = queuedServiceIds.length
    ? queuedServiceIds
    : (hasClearedServices ? [] : (typeof service_id === 'number' ? [service_id] : undefined))

  const {
    updatedEntities: {
      agencies: agenciesUpdatedAt = 0,
      brandThemes: brandThemesUpdatedAt = 0,
      providers: providersUpdatedAt = 0,
      publications: publicationsUpdatedAt = 0,
      services: servicesUpdatedAt = 0,
      serviceProducts: serviceProductsUpdatedAt = 0,
      templateGroups: templateGroupsUpdatedAt = 0,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  // Entities
  // Agencies
  const agenciesPayload = useAgencies({
    filters: {
      brand_id,
      string,
      limit: 500,
    },
    performHttpRequests: !!brand_id && !string,
  })

  const { filteredAgencies } = agenciesPayload

  // Brand Themes
  const brandThemesPayload = useBrandThemes({
    filters: {
      brand_id,
      limit: 500,
      string,
    },
    performHttpRequests: activeStepKey === 'brandThemeIds' && !!brand_id && !string,
  })

  const { filteredBrandThemes } = brandThemesPayload

  // Providers
  const providersPayload = useProviders({
    filters: {
      assigned_brand_id: brand_id,
      string,
      limit: 500,
    },
    performHttpRequests: !!brand_id && !string,
    requestOptions: {
      include_provider_assigned_brand_ids: true,
      include_provider_service_ids: true,
    },
  })

  const { filteredProviders } = providersPayload
  const hasQueuedProviders = !!queuedProviderIds.length

  // Publications
  const publicationsPayload = usePublications({
    filters: {
      provider_ids: queuedProviderIds,
      string,
    },
    performHttpRequests: activeStepKey === 'publicationIds' && hasQueuedProviders && !string,
    requestOptions: {},
  })

  const { filteredPublications } = publicationsPayload

  // Services
  const servicesPayload = useServices({
    filters: {
      string,
    },
    performHttpRequests: activeStepKey === 'serviceIds' && !string,
  })

  const { filteredServices } = servicesPayload

  // Service Products
  const serviceProductsPayload = useServiceProducts({
    filters: {
      provider_ids: queuedProviderIds,
      limit: 500,
      service_ids: serviceProductServiceIds,
      string,
    },
    performHttpRequests: hasQueuedProviders && (Array.isArray(serviceProductServiceIds) || !!service_id) && !string,
    requestOptions: {
      include_service_product_provider_ids: true,
    },
  })

  const { filteredServiceProducts } = serviceProductsPayload

  // Template Groups
  const templateGroupsPayload = useTemplateGroups({
    filters: {
      brand_id,
      string,
    },
    performHttpRequests: activeStepKey === 'templateGroupIds' && !!brand_id && !string,
  })

  const { filteredTemplateGroups } = templateGroupsPayload

  // Cache Key
  // @ts-expect-error TS2365
  const entitiesUpdatedAt = agenciesUpdatedAt
    + brandThemesUpdatedAt
    + providersUpdatedAt
    + publicationsUpdatedAt
    + servicesUpdatedAt
    + serviceProductsUpdatedAt
    + templateGroupsUpdatedAt

  return {
    entitiesUpdatedAt,
    filteredEntities: {
      agencies: filteredAgencies,
      brandThemes: filteredBrandThemes,
      providers: filteredProviders,
      publications: filteredPublications,
      services: filteredServices,
      serviceProducts: filteredServiceProducts,
      templateGroups: filteredTemplateGroups,
    },
    payloads: {
      agencies: agenciesPayload,
      brandThemes: brandThemesPayload,
      providers: providersPayload,
      publications: publicationsPayload,
      services: servicesPayload,
      serviceProducts: serviceProductsPayload,
      templateGroups: templateGroupsPayload,
    },
  }
}

export default useScopedEntities
